import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchBackups,
  createBackup,
  fetchBackupPreview,
  restoreBackup,
  deleteBackup,
  type BackupMeta,
  type BackupPreview,
} from '../../api/games';
import { useConfirm } from '../../context/ConfirmDialogContext';

interface Props {
  gameId: string | null;
  gameName: string;
  onClose: () => void;
}

const DRAWER_WIDTH = 480;

function fmtBytes(b: number): string {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(0)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

// ── Restore confirm dialog with preview ──────────────────────────────────

function RestoreDialog({
  gameId,
  backup,
  onClose,
  onConfirm,
}: {
  gameId: string;
  backup: BackupMeta;
  onClose: () => void;
  onConfirm: (restartAfter: boolean) => void;
}) {
  const [restartAfter, setRestartAfter] = useState(true);

  const { data: preview, isLoading } = useQuery<BackupPreview>({
    queryKey: ['backup-preview', gameId, backup.ts],
    queryFn: () => fetchBackupPreview(gameId, backup.ts),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Restore backup {backup.ts}?</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will stop the server and overwrite current save data.
        </Alert>
        <FormControlLabel
          control={
            <Switch
              checked={restartAfter}
              onChange={(e) => setRestartAfter(e.target.checked)}
              size="small"
            />
          }
          label="Restart server after restore"
          sx={{ mb: 1 }}
        />
        {isLoading && <CircularProgress size={20} />}
        {preview && (
          <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1, fontSize: '0.75rem', fontFamily: 'monospace' }}>
            {preview.entries.slice(0, 50).map((e) => (
              <div key={e.path}>{e.path}</div>
            ))}
            {preview.truncated && <div>… and {preview.total_files - preview.entries.length} more</div>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={() => onConfirm(restartAfter)}>
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main drawer ────────────────────────────────────────────────────────────

export function BackupDrawer({ gameId, gameName, onClose }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const open = gameId !== null;

  const { data, isLoading, error } = useQuery({
    queryKey: ['backups', gameId],
    queryFn: () => fetchBackups(gameId!),
    enabled: open,
    refetchInterval: 30_000,
  });

  const backups = data?.backups ?? [];

  const createMutation = useMutation({
    mutationFn: () => createBackup(gameId!),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['backups', gameId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (ts: string) => deleteBackup(gameId!, ts),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['backups', gameId] }),
  });

  const restoreMutation = useMutation({
    mutationFn: ({ ts, restartAfter }: { ts: string; restartAfter: boolean }) =>
      restoreBackup(gameId!, ts, restartAfter),
  });

  const [restoreTarget, setRestoreTarget] = useState<BackupMeta | null>(null);

  const handleDelete = async (ts: string) => {
    const ok = await confirm(`Delete backup ${ts}? This cannot be undone.`, {
      title: 'Delete backup?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) deleteMutation.mutate(ts);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: DRAWER_WIDTH } } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, minHeight: 56 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
            Backups — {gameName}
          </Typography>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Tooltip>
        </Stack>
        <Divider />

        <Box sx={{ px: 2, py: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={createMutation.isPending ? <CircularProgress size={14} /> : <AddIcon />}
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
          >
            Create Backup Now
          </Button>
          {createMutation.isSuccess && (
            <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
              Backup created!
            </Typography>
          )}
          {createMutation.isError && (
            <Typography variant="caption" color="error.main" sx={{ ml: 1 }}>
              Failed to create backup.
            </Typography>
          )}
        </Box>
        <Divider />

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>Failed to load backups.</Alert>}
        {!isLoading && backups.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No backups yet.
          </Typography>
        )}

        <List dense>
          {backups.map((b) => (
            <ListItem
              key={b.ts}
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Restore">
                    <IconButton
                      size="small"
                      onClick={() => setRestoreTarget(b)}
                      disabled={restoreMutation.isPending}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => void handleDelete(b.ts)}
                      disabled={deleteMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            >
              <ListItemText
                primary={fmtDate(b.created_at)}
                secondary={fmtBytes(b.size_bytes)}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {restoreTarget && gameId && (
        <RestoreDialog
          gameId={gameId}
          backup={restoreTarget}
          onClose={() => setRestoreTarget(null)}
          onConfirm={(restartAfter) => {
            restoreMutation.mutate({ ts: restoreTarget.ts, restartAfter });
            setRestoreTarget(null);
          }}
        />
      )}
    </>
  );
}
